// Ruler guides for the 16:9 Slide Designer — Illustrator/Photoshop-style
// draggable guide lines. Guides are stored in the same percent-of-content
// coordinate space as block frames (see slideFrames.js):
//   - `v` holds VERTICAL guide lines, each an x position (0-100)
//   - `h` holds HORIZONTAL guide lines, each a y position (0-100)
// Guides are purely an editing aid: they live only in the designer overlay,
// are never written to block/slide data, and therefore never export or render
// in the presentation.

const GUIDES_KEY = 'lp1.slideGuides.v1';
const RULERS_KEY = 'lp1.slideRulers.v1';

// How close (in percent of the slide) a block edge/center must come to a guide
// before it snaps onto it. Matches the designer's built-in center/margin snap.
export const GUIDE_SNAP_TOLERANCE = 1.2;

export const emptyGuides = () => ({ v: [], h: [] });

const clampPos = (value) => Math.min(100, Math.max(0, value));

const sanitizeList = (list) => {
    if (!Array.isArray(list)) return [];
    return list
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n >= 0 && n <= 100)
        .map((n) => Math.round(n * 100) / 100);
};

export const sanitizeGuides = (guides) => ({
    v: sanitizeList(guides && guides.v),
    h: sanitizeList(guides && guides.h)
});

// Round a dropped guide to a tidy position: whole percents, and snap onto the
// exact center (50%) when it lands close, so a "middle" guide is easy to place.
export const tidyGuidePosition = (pos) => {
    const clamped = clampPos(pos);
    if (Math.abs(clamped - 50) <= 1) return 50;
    return Math.round(clamped);
};

const hasStorage = () => {
    try {
        return typeof window !== 'undefined' && !!window.localStorage;
    } catch (err) {
        return false;
    }
};

export const loadGuides = () => {
    if (!hasStorage()) return emptyGuides();
    try {
        const raw = window.localStorage.getItem(GUIDES_KEY);
        if (!raw) return emptyGuides();
        return sanitizeGuides(JSON.parse(raw));
    } catch (err) {
        return emptyGuides();
    }
};

export const saveGuides = (guides) => {
    if (!hasStorage()) return;
    try {
        window.localStorage.setItem(GUIDES_KEY, JSON.stringify(sanitizeGuides(guides)));
    } catch (err) {
        /* ignore quota/serialization errors — guides are a non-critical aid */
    }
};

export const loadRulerPref = () => {
    if (!hasStorage()) return false;
    try {
        return window.localStorage.getItem(RULERS_KEY) === '1';
    } catch (err) {
        return false;
    }
};

export const saveRulerPref = (on) => {
    if (!hasStorage()) return;
    try {
        window.localStorage.setItem(RULERS_KEY, on ? '1' : '0');
    } catch (err) {
        /* ignore */
    }
};

// Snap a block frame's position so its left/center/right edge lands on the
// nearest vertical guide, and its top/middle/bottom edge on the nearest
// horizontal guide, when within `tol`. Returns a new frame; size is untouched.
export const snapFrameToGuides = (frame, guides, tol = GUIDE_SNAP_TOLERANCE) => {
    if (!frame || !guides) return frame;
    const out = { ...frame };
    const vs = Array.isArray(guides.v) ? guides.v : [];
    const hs = Array.isArray(guides.h) ? guides.h : [];

    let bestX = null;
    for (const g of vs) {
        const anchors = [
            { d: g - out.x, x: g },
            { d: g - (out.x + out.w / 2), x: g - out.w / 2 },
            { d: g - (out.x + out.w), x: g - out.w }
        ];
        for (const a of anchors) {
            if (Math.abs(a.d) <= tol && (bestX === null || Math.abs(a.d) < Math.abs(bestX.d))) {
                bestX = a;
            }
        }
    }
    if (bestX) out.x = bestX.x;

    let bestY = null;
    for (const g of hs) {
        const anchors = [
            { d: g - out.y, y: g },
            { d: g - (out.y + out.h / 2), y: g - out.h / 2 },
            { d: g - (out.y + out.h), y: g - out.h }
        ];
        for (const a of anchors) {
            if (Math.abs(a.d) <= tol && (bestY === null || Math.abs(a.d) < Math.abs(bestY.d))) {
                bestY = a;
            }
        }
    }
    if (bestY) out.y = bestY.y;

    return out;
};
